import { alertVariants } from "@visiblethread/react-components";
import localhost, { downloadFileFromResponse } from "apis/localhost";
import {
  ITEMS_PER_PAGE,
  MULTIPART_FORM_DATA_CONTENT_TYPE,
} from "constants/app";
import { documentBy, saveButtonError } from "constants/discovery";
import { modalTypes } from "constants/types";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal } from "slices/app/actions/modals";
import {
  addToJobQueue,
  clearJobQueue,
  disableNavBarAndSideBar,
  hideGlobalAlert,
  removeFromJobQueue,
  showGlobalAlert,
  showInlineAlert,
} from "slices/app/appSlice";
import { getByContentData } from "slices/discovery/actions/byContent";
import { applyFilter } from "slices/discovery/actions/filters";
import { fetchSnapshots } from "slices/discovery/actions/snapshot";
import {
  fetchThemes,
  fetchUniqueItemsCount,
  searchItems,
} from "slices/discovery/actions/theme";
import {
  cancelEdits,
  setEditMode,
  setIgnoreList,
  setIgnoreListWithFreq,
  setLocked,
} from "slices/settings/slices/discoveryIgnoreSlice";

export const cancelChanges = () => (dispatch) => {
  dispatch(cancelEdits());
  dispatch(setEditMode(false));
  dispatch(disableNavBarAndSideBar(false));
};

export const fetchDiscoveryIgnoreList = () => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/discoveryIgnoreList/")
    .then((response) => {
      const { data } = response;
      dispatch(setIgnoreList(data));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const openEditMode = () => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/discoveryIgnoreList/lock")
    .then(() => {
      dispatch(setEditMode(true));
      dispatch(disableNavBarAndSideBar(true));
    })
    .catch((error) => {
      const message = error.response.data.error;
      if (message.includes("is currently editing it")) {
        dispatch(setLocked(true));
        dispatch(
          showInlineAlert({ variant: alertVariants.warning, text: message })
        );
      } else {
        dispatch(showErrorModal(error));
      }
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const closeEditMode = () => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/discoveryIgnoreList/unlock")
    .then(() => {
      dispatch(cancelChanges());
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const setDiscoveryIgnoreList = (ignoreList) => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .post(
      "/discoveryIgnoreList/edit",
      ignoreList
        .filter((ignoreListItem) => ignoreListItem.term)
        .map((ignoreListItem) => ignoreListItem.term)
    )
    .then((response) => {
      const { data } = response;
      dispatch(setIgnoreList(data));
      dispatch(setEditMode(false));
      dispatch(disableNavBarAndSideBar(false));
    })
    .catch((error) => {
      if (error.response?.status === 409) {
        dispatch(
          showGlobalAlert({
            variant: alertVariants.warning,
            text: saveButtonError,
          })
        );
      } else {
        dispatch(showErrorModal(error));
      }
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const getDiscoveryIgnoreListWithFreq =
  () => async (dispatch, getState) => {
    dispatch(addToJobQueue("getDiscoveryIgnoreListWithFreq"));
    const documentIds = [];
    const { folderId, documentId } = getState().discovery.config;
    if (!folderId && !documentId) {
      return;
    }

    if (folderId && !documentId) {
      const { themes } = getState().discovery;
      if (!themes.length) {
        return;
      }
      const selectedDocs = themes[0].Documents?.filter((doc) => !doc.ignored);

      selectedDocs?.forEach((doc) => {
        documentIds.push(doc.Id);
      });
    } else {
      documentIds.push(documentId);
    }

    await localhost
      .post("/discoveryIgnoreList/ignoredTerms", {
        folderId,
        documentIds,
      })
      .then((response) => {
        const { data } = response;
        if (typeof data === "string") {
          setTimeout(() => {
            dispatch(getDiscoveryIgnoreListWithFreq());
          }, 250);
        } else {
          dispatch(setIgnoreListWithFreq(data));
        }
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      })
      .finally(() => {
        dispatch(removeFromJobQueue("getDiscoveryIgnoreListWithFreq"));
      });
  };

export const exportDiscoveryIgnoreList = () => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .get("/discoveryIgnoreList/export", {
      responseType: "blob",
    })
    .then(async (response) => {
      const { data } = response;
      downloadFileFromResponse(data, "DiscoveryIgnoreList.csv");
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(clearJobQueue());
    });
};

export const importDiscoveryIgnoreList = (formData) => async (dispatch) => {
  dispatch(addToJobQueue("genericJob"));

  await localhost
    .post("/discoveryIgnoreList/import", formData, {
      headers: {
        "Content-Type": MULTIPART_FORM_DATA_CONTENT_TYPE,
      },
    })
    .then(async (response) => {
      const { data } = response;
      dispatch(setIgnoreList(data));
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(hideModal(modalTypes.IMPORT_FILE));
      dispatch(clearJobQueue());
    });
};

const getText = (terms) =>
  `Term${terms.length > 1 ? "s" : ""} successfully removed from the Discovery Ignore List`;

export const deleteTerms = (terms) => async (dispatch, getState) => {
  dispatch(addToJobQueue("deleteTerms"));
  dispatch(hideGlobalAlert());
  const {
    documentBy: docBy,
    filterType,
    searchTerm,
  } = getState().discovery.config;
  const { folderId, documentId } = getState().folders;
  const byContent = docBy === documentBy.content;

  await localhost
    .delete("/discoveryIgnoreList/delete", { data: terms })
    .then(async () => {
      dispatch(getDiscoveryIgnoreListWithFreq());
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: getText(terms),
        })
      );
      if (byContent) {
        dispatch(getByContentData(folderId, documentId, ITEMS_PER_PAGE));
      } else {
        dispatch(fetchSnapshots());
        await dispatch(fetchThemes());
        if (filterType) {
          dispatch(applyFilter(filterType));
          dispatch(fetchUniqueItemsCount(filterType));
        } else if (searchTerm) {
          dispatch(searchItems(searchTerm));
          dispatch(fetchUniqueItemsCount(filterType));
        }
      }
    })
    .catch((error) => {
      dispatch(showErrorModal(error));
    })
    .finally(() => {
      dispatch(removeFromJobQueue("deleteTerms"));
    });
};
